import React, { useEffect, useMemo, useState } from 'react';
import { useSportState } from '@apollo/core/src/state/sport/sport';
import { useHistory } from 'react-router-dom';
import { at, isEmpty } from 'lodash';
import cx from 'classnames';
import Label from '../../../shared/components/I18n/Label';
import Logo from '../../../shared/components/Logo/Logo';
import { getEventDate, groupEventsByDate } from '../../../core/utils';
import useTranslate from '../../../shared/components/I18n/Interpreter';
import { getEventUrl } from '../../../shared/components/Sport/SportLinks';
import Dropdown from '../../../shared/components/Dropdown/Dropdown';
import { AnimatedDropdown } from '../../Animations/AnimatedDropdown';
import useScrollPage from '../../../shared/components/Dropdown/useScrollPage';

const EventListSelect = ({ selectedEvent, events, onEventChange }) => {
  const t = useTranslate();

  const [sportsState] = useSportState();
  const history = useHistory();

  const [isShowDropdown, setIsShowDropdown] = useState(false);
  const { sportsById, tournamentsById, categoriesById } = sportsState;
  const tournament = useMemo(
    () => selectedEvent && tournamentsById && tournamentsById[selectedEvent.tournamentId],
    [selectedEvent, tournamentsById],
  );

  const [homeName, homeId, homeLogoUri, awayName, awayId, awayLogoUri] = at(selectedEvent, [
    'participants.home.fullName',
    'participants.home.id',
    'participants.home.logoUri',
    'participants.away.fullName',
    'participants.away.id',
    'participants.away.logoUri',
  ]);

  const onClick = (eventId) => {
    const event = events.find((e) => e.id === eventId);
    if (!event) {
      return null;
    }
    const sport = sportsById && sportsById[event.sportId];
    const category = categoriesById && categoriesById[event.categoryId];

    const url = getEventUrl({ sport, category, tournament, event });

    onEventChange(event); // show selected event immediately
    history.push(url);
  };

  const isScroll = useScrollPage();

  useEffect(() => {
    if (isScroll) {
      setIsShowDropdown(false);
    }
  }, [isScroll]);

  const eventsByDate = useMemo(() => groupEventsByDate(events), [events]);

  return (
    <div className='event-list-select'>
      {/* tournament ? <div className='event-list-select-tournament'>{tournament.name}</div> : null */}
      <Dropdown
        isExpanded={isShowDropdown}
        onExpanded={setIsShowDropdown}
        className='event-list-select-wrapper'
      >
        <h1
          className={cx('event-name', { expanded: isShowDropdown })}
          title={`${homeName} - ${awayName}`}
        >
          <div className='event-participants__participant'>
            <Logo
              participantId={homeId}
              logoUri={homeLogoUri}
              size='lg'
              alt={homeName}
              isHideEmpty
            />
            <div className='event-participants__participant-name'>{homeName}</div>
          </div>
          <span>
            <Label message='versus' />
          </span>
          <div className='event-participants__participant'>
            <Logo
              participantId={awayId}
              logoUri={awayLogoUri}
              size='lg'
              alt={awayName}
              isHideEmpty
            />
            <div className='event-participants__participant-name'>{awayName}</div>
          </div>
        </h1>
        <AnimatedDropdown
          isExpanded={isShowDropdown}
          className='event-select__dropdown'
          endOverflow='auto'
        >
          {!isEmpty(eventsByDate) ? (
            <>
              {eventsByDate.map((eventsGroup) => (
                <div key={eventsGroup[0].id} className='event-select__group'>
                  <div className='event-select__title'>
                    {getEventDate(eventsGroup[0].startTime, t)}
                  </div>
                  {eventsGroup.map((event) => (
                    <div
                      key={event.id}
                      onClick={() => {
                        onClick(event.id);
                      }}
                      className={cx([
                        'event-select__item',
                        event.id === selectedEvent?.id && 'active',
                      ])}
                    >
                      <span className='event-select__text'>{event.name}</span>
                    </div>
                  ))}
                </div>
              ))}
            </>
          ) : null}
        </AnimatedDropdown>
      </Dropdown>
    </div>
  );
};

export default EventListSelect;
